import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ActionCardMetadata } from '../../generated/chatActionTypes'
import { getGatewayConfigs, type GatewayConfig } from '../../api/client'
import { notifyGatewayConfigChanged } from '../../hooks/useVideoModels'
import { createNadDebug } from '../../utils/debugLog'
import GatewaySettingsModal from '../settings/GatewaySettingsModal'

type ChatAction = ActionCardMetadata['action']
interface Destination {
  title: string
  description: string
  label: string
  open: (action: ChatAction, interactionId: string) => Promise<void>
}
const log = createNadDebug('director-action-card')

/** Add destinations here and in the shared schema; the card/dispatch stays generic. */
export function useActionDestinations(cardId: string) {
  const { t } = useTranslation('chat')
  const navigate = useNavigate()
  const [model, setModel] = useState<GatewayConfig | null>(null)
  const destinations: Record<ChatAction['destination'], Destination> = {
    byom_model: {
      title: t('actionCard.byom.title'),
      description: t('actionCard.byom.description'),
      label: t('actionCard.byom.open'),
      async open(action, interactionId) {
        if (action.destination !== 'byom_model') throw new Error(t('actionCard.invalid'))
        // Fetch current, owner-scoped state only on click; never carry credentials in chat.
        const configs = await getGatewayConfigs()
        const config = configs.find(item => item.id === action.config_id && (item.model_kind ?? 'video') === 'video')
        if (!config) throw new Error(t('actionCard.byom.missing'))
        setModel(config)
        log({ event: 'action-card:target-config-loaded', card_id: cardId, interaction_id: interactionId, config_id: config.id, needs_api_key: config.needs_api_key === true })
      },
    },
    dashboard: {
      title: t('actionCard.dashboard.title'),
      description: t('actionCard.dashboard.description'),
      label: t('actionCard.dashboard.open'),
      async open() { navigate('/dashboard') },
    },
  }
  const destinationView = model && (
    <GatewaySettingsModal
      isOpen
      editConfig={model}
      onClose={() => setModel(null)}
      onSaved={() => {
        notifyGatewayConfigChanged()
        log({ event: 'action-card:model-saved', card_id: cardId, config_id: model.id })
        setModel(null)
      }}
    />
  )
  return { destinations, destinationView }
}
